import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import {
  ExternalLink,
  Globe,
  Search,
  Download,
  MoreHorizontal,
  Copy,
  Eye,
  RefreshCw,
  Play,
  Users,
  Eye as EyeIcon,
  Percent,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

interface ReferenceRow {
  url: string;
  plays: number;
  uniquePlays: number;
  impressions: number;
  uniqueImpressions: number;
  playRate: number;
}

const formatNumber = (n: number) => n.toLocaleString(getInterfaceLanguage());

const getDomain = (url: string) => {
  try {
    return new URL(url).hostname || url;
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }
};

// Converte o page_origin cru (hostname, 'DIRECT' ou 'Desconhecido') vindo do
// backend numa URL exibível/navegável. Hostname real vira https://<host>;
// 'DIRECT'/'Desconhecido' viram rótulos não navegáveis.
const buildOriginUrl = (raw?: string): string => {
  if (!raw || raw === 'Desconhecido') return 'Desconhecido';
  if (raw === 'DIRECT') return translateInterfaceText("Direto / sem origem");
  return `https://${raw}`;
};

const fmtDayMonth = (isoDate: string) => {
  const [, m, d] = isoDate.split('-');
  return `${d}/${m}`;
};

const fmtMonthYear = (yearMonth: string) => {
  const [y, m] = yearMonth.split('-');
  return `${m}/${y}`;
};

// Chave ISO de semana (YYYY-Www) para agrupamento semanal.
const isoWeekKey = (isoDate: string) => {
  const dt = new Date(`${isoDate}T00:00:00`);
  const tmp = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  const dayNum = (tmp.getUTCDay() + 6) % 7;
  tmp.setUTCDate(tmp.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(
      ((tmp.getTime() - firstThursday.getTime()) / 86400000 -
        3 +
        ((firstThursday.getUTCDay() + 6) % 7)) /
        7,
    );
  return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
};

interface ChartPoint {
  date: string;
  plays: number;
  uniquePlays: number;
  impressions: number;
  uniqueImpressions: number;
}

// Agrega a série diária do vídeo (viewsSeries) na granularidade escolhida.
const buildChartData = (
  viewsSeries: any[],
  granularity: 'daily' | 'weekly' | 'monthly',
): ChartPoint[] => {
  const items = (viewsSeries || []).map((d) => ({
    raw: d.date as string,
    plays: d.plays_total || 0,
    uniquePlays: d.plays_unique || 0,
    impressions: d.views_total || 0,
    uniqueImpressions: d.views_unique || 0,
  }));

  if (granularity === 'daily') {
    return items.map((d) => ({
      date: fmtDayMonth(d.raw),
      plays: d.plays,
      uniquePlays: d.uniquePlays,
      impressions: d.impressions,
      uniqueImpressions: d.uniqueImpressions,
    }));
  }

  const buckets = new Map<string, { first: string; plays: number; uniquePlays: number; impressions: number; uniqueImpressions: number }>();
  for (const d of items) {
    const key = granularity === 'monthly' ? d.raw.slice(0, 7) : isoWeekKey(d.raw);
    const b = buckets.get(key) || { first: d.raw, plays: 0, uniquePlays: 0, impressions: 0, uniqueImpressions: 0 };
    b.plays += d.plays;
    b.uniquePlays += d.uniquePlays;
    b.impressions += d.impressions;
    b.uniqueImpressions += d.uniqueImpressions;
    if (d.raw < b.first) b.first = d.raw;
    buckets.set(key, b);
  }

  return Array.from(buckets.entries())
    .sort((a, b) => a[1].first.localeCompare(b[1].first))
    .map(([key, b]) => ({
      date: granularity === 'monthly' ? fmtMonthYear(key) : fmtDayMonth(b.first),
      plays: b.plays,
      uniquePlays: b.uniquePlays,
      impressions: b.impressions,
      uniqueImpressions: b.uniqueImpressions,
    }));
};

const PlayRateBadge: React.FC<{ value: number }> = ({ value }) => {
  useLanguage();
  const cls = value >= 50 ? 'bg-emerald-50 text-emerald-700' : 'bg-[#EEF4FF] text-[#2563FF]';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>
      {value.toFixed(1).replace('.', ',')}%
    </span>
  );
};

interface SummaryCardProps {
  title: string;
  value: string;
  desc: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}
const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, desc, icon, iconBg, iconColor }) => { useLanguage(); return ((
  <div className="bg-white rounded-2xl border border-[#E5EAF3] p-[18px] flex flex-col gap-2">
    <div className="flex items-start justify-between">
      <span className="text-[13px] text-[#64748B] font-medium">{title}</span>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}>
        <span className={iconColor}>{icon}</span>
      </span>
    </div>
    <div className="text-[24px] font-semibold text-[#0F172A] tracking-tight leading-none">{value}</div>
    <div className="text-[12px] text-[#64748B]">{desc}</div>
  </div>
)); };

type SortKey = 'url' | 'plays' | 'uniquePlays' | 'impressions' | 'uniqueImpressions' | 'playRate';

const SortableTh = ({
  label,
  sortKey,
  sortBy,
  sortDir,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  onClick: (k: SortKey) => void;
}) => {
  useLanguage();
  const active = sortBy === sortKey;
  return (
    <th className={`py-3 font-medium ${sortKey === 'url' ? 'text-left' : 'text-right'}`}>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`inline-flex items-center gap-1 transition-colors ${
          active ? 'text-[#2563FF]' : 'text-[#64748B] hover:text-[#0F172A]'
        }`}
      >
        {label}
        <span className="inline-flex flex-col -space-y-[3px] ml-0.5">
          <ArrowUp
            className={`w-2.5 h-2.5 ${
              active && sortDir === 'asc' ? 'text-[#2563FF]' : 'text-[#CBD5E1]'
            }`}
            strokeWidth={3}
          />
          <ArrowDown
            className={`w-2.5 h-2.5 ${
              active && sortDir === 'desc' ? 'text-[#2563FF]' : 'text-[#CBD5E1]'
            }`}
            strokeWidth={3}
          />
        </span>
      </button>
    </th>
  );
};

interface ReferenceTabContentProps {
  date?: DateRange;
  videoId: string | null;
  metricsEnhanced?: any;
}

export function ReferenceTabContent({ metricsEnhanced }: ReferenceTabContentProps) {
  useLanguage();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('plays');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [granularity, setGranularity] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [detailRow, setDetailRow] = useState<ReferenceRow | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Linhas reais a partir do bloco references do backend (agregado por hostname).
  const rows: ReferenceRow[] = useMemo(() => {
    const references = metricsEnhanced?.references || [];
    return references.map((ref: any) => {
      const impressions = ref.views || 0;
      const plays = ref.plays || 0;
      return {
        url: buildOriginUrl(ref.page_origin),
        plays,
        uniquePlays: ref.uniquePlays || 0,
        impressions,
        uniqueImpressions: ref.uniqueViews || 0,
        playRate: impressions > 0 ? parseFloat(((plays / impressions) * 100).toFixed(1)) : 0,
      };
    });
  }, [metricsEnhanced?.references]);

  const chartData = useMemo(
    () => buildChartData(metricsEnhanced?.viewsSeries || [], granularity),
    [metricsEnhanced?.viewsSeries, granularity],
  );

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const totals = useMemo(() => {
    const plays = rows.reduce((a, r) => a + r.plays, 0);
    const uniquePlays = rows.reduce((a, r) => a + r.uniquePlays, 0);
    const impressions = rows.reduce((a, r) => a + r.impressions, 0);
    const playRate = impressions > 0 ? (plays / impressions) * 100 : 0;
    return { plays, uniquePlays, impressions, playRate };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase().trim();
    let result = rows.filter((r) => !q || r.url.toLowerCase().includes(q));
    result = [...result].sort((a, b) => {
      if (sortBy === 'url') {
        const diff = a.url.localeCompare(b.url);
        return sortDir === 'asc' ? diff : -diff;
      }
      const diff = (a[sortBy] as number) - (b[sortBy] as number);
      return sortDir === 'asc' ? diff : -diff;
    });
    return result;
  }, [rows, search, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedRows = useMemo(
    () => filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredRows, safePage]
  );

  React.useEffect(() => {
    setPage(1);
  }, [search, sortBy, sortDir]);

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success(translateInterfaceText("URL copiada"));
  };

  const handleOpen = (url: string) => {
    if (url.startsWith('http')) window.open(url, '_blank', 'noopener,noreferrer');
    else toast.info(translateInterfaceText("URL não navegável"));
  };

  const exportCsv = () => {
    const header = ['URL', 'Plays', 'Plays únicos', 'Visualizações', 'Visualizações únicas', 'Play Rate']
      .map((label) => translateInterfaceText(label)).join(';') + '\n';
    const csvRows = filteredRows
      .map((r) => `${r.url};${r.plays};${r.uniquePlays};${r.impressions};${r.uniqueImpressions};${r.playRate}%`)
      .join('\n');
    const blob = new Blob([header + csvRows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'referencias.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const topShare = detailRow && totals.plays > 0 ? (detailRow.plays / totals.plays) * 100 : 0;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title={translateInterfaceText("Plays")}
            value={formatNumber(totals.plays)}
            desc={translateInterfaceText("Total vindo de referências")}
            icon={<Play className="w-4 h-4" />}
            iconBg="bg-[#EEF4FF]"
            iconColor="text-[#4B7BFF]"
          />
          <SummaryCard
            title={translateInterfaceText("Plays únicos")}
            value={formatNumber(totals.uniquePlays)}
            desc={translateInterfaceText("Usuários únicos")}
            icon={<Users className="w-4 h-4" />}
            iconBg="bg-[#EEF4FF]"
            iconColor="text-[#4B7BFF]"
          />
          <SummaryCard
            title={translateInterfaceText("Visualizações")}
            value={formatNumber(totals.impressions)}
            desc="Carregamentos do player"
            icon={<EyeIcon className="w-4 h-4" />}
            iconBg="bg-[#EEF4FF]"
            iconColor="text-[#4B7BFF]"
          />
          <SummaryCard
            title={translateInterfaceText("Play Rate")}
            value={`${totals.playRate.toFixed(1).replace('.', ',')}%`}
            desc={translateInterfaceText("Média das referências")}
            icon={<Percent className="w-4 h-4" />}
            iconBg="bg-[#EEF4FF]"
            iconColor="text-[#4B7BFF]"
          />
        </div>

        {/* Chart card */}
        <div className="bg-white rounded-[20px] border border-[#E5EAF3] p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-[16px] font-semibold text-[#0F172A]">{translateInterfaceText("Evolução das referências")}</h3>
              <p className="text-[13px] text-[#64748B] mt-0.5">
                {translateInterfaceText("Compare plays, impressões e usuários únicos por fonte de tráfego.")}</p>
            </div>
            <Select value={granularity} onValueChange={(v) => setGranularity(v as any)}>
              <SelectTrigger className="w-[140px] h-9 text-sm bg-white border-[#E5EAF3]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">{translateInterfaceText("Diário")}</SelectItem>
                <SelectItem value="weekly">{translateInterfaceText("Semanal")}</SelectItem>
                <SelectItem value="monthly">{translateInterfaceText("Mensal")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="h-[260px] md:h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <RTooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #E5EAF3',
                    borderRadius: 12,
                    boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} iconType="circle" />
                <Line type="monotone" dataKey="plays" name="Plays" stroke="#2563FF" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="uniquePlays" name="Plays únicos" stroke="#10B981" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="impressions" name="Visualizações" stroke="#F59E0B" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="uniqueImpressions" name="Visualizações únicas" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table card */}
        <div className="bg-white rounded-[20px] border border-[#E5EAF3] p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-[16px] font-semibold text-[#0F172A]">{translateInterfaceText("URLs de referência")}</h3>
              <p className="text-[13px] text-[#64748B] mt-0.5">
                {translateInterfaceText("Veja quais páginas enviaram tráfego para este vídeo.")}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportCsv} className="h-9 border-[#E5EAF3]">
                <Download className="w-4 h-4 mr-1.5" />
                {translateInterfaceText("Exportar CSV")}</Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={translateInterfaceText("Buscar URL, domínio ou página")}
              className="h-11 pl-10 rounded-xl border-[#E5EAF3] focus-visible:ring-[#2563FF] focus-visible:border-[#2563FF] bg-white"
            />
          </div>

          {filteredRows.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm table-fixed">
                  <colgroup>
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <thead>
                    <tr className="text-left text-[13px] text-[#64748B]">
                      <th className="py-3 font-medium text-left">{translateInterfaceText("URL")}</th>
                      <SortableTh label={translateInterfaceText("Plays")} sortKey="plays" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                      <SortableTh label={translateInterfaceText("Plays únicos")} sortKey="uniquePlays" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                      <SortableTh label={translateInterfaceText("Visualizações")} sortKey="impressions" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                      <SortableTh label={translateInterfaceText("Visualizações únicas")} sortKey="uniqueImpressions" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                      <SortableTh label={translateInterfaceText("Play rate")} sortKey="playRate" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                      <th className="py-3 font-medium text-right pl-2">{translateInterfaceText("Ações")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row) => (
                      <tr key={row.url} className="border-t border-[#EEF2F7] hover:bg-[#F9FAFC]">
                        <td className="py-3 pr-3 max-w-[340px]">
                          <div className="flex items-center gap-2 min-w-0">
                            <Globe className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="truncate text-[#0F172A]">{getDomain(row.url)}</span>
                              </TooltipTrigger>
                              <TooltipContent>{row.url}</TooltipContent>
                            </Tooltip>
                          </div>
                        </td>
                        <td className="py-3 text-right text-[#334155] font-medium">{formatNumber(row.plays)}</td>
                        <td className="py-3 text-right text-[#334155] font-medium">{formatNumber(row.uniquePlays)}</td>
                        <td className="py-3 text-right text-[#334155] font-medium">{formatNumber(row.impressions)}</td>
                        <td className="py-3 text-right text-[#334155] font-medium">{formatNumber(row.uniqueImpressions)}</td>
                        <td className="py-3 text-right">
                          <PlayRateBadge value={row.playRate} />
                        </td>
                        <td className="py-3 pl-2">
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => handleOpen(row.url)}
                                  className="p-1.5 rounded-md text-[#94A3B8] hover:text-[#2563FF] hover:bg-[#EEF2FF] transition-colors"
                                  aria-label={translateInterfaceText("Abrir em nova guia")}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>{translateInterfaceText("Abrir em nova guia")}</TooltipContent>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {pagedRows.map((row) => (
                  <div key={row.url} className="rounded-2xl border border-[#E5EAF3] p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Globe className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                        <span className="truncate text-[14px] text-[#0F172A] font-medium">{getDomain(row.url)}</span>
                        <button
                          type="button"
                          onClick={() => handleOpen(row.url)}
                          className="flex-shrink-0 p-1 rounded-md text-[#94A3B8] hover:text-[#2563FF] hover:bg-[#EEF2FF] transition-colors"
                          aria-label={translateInterfaceText("Abrir URL")}
                        >
                          <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-2 text-[13px] mb-3">
                      <div className="text-[#64748B]">{translateInterfaceText("Plays")}</div>
                      <div className="text-right text-[#334155] font-medium">{formatNumber(row.plays)}</div>
                      <div className="text-[#64748B]">{translateInterfaceText("Plays únicos")}</div>
                      <div className="text-right text-[#334155] font-medium">{formatNumber(row.uniquePlays)}</div>
                      <div className="text-[#64748B]">{translateInterfaceText("Visualizações")}</div>
                      <div className="text-right text-[#334155] font-medium">{formatNumber(row.impressions)}</div>
                      <div className="text-[#64748B]">{translateInterfaceText("Play Rate")}</div>
                      <div className="text-right"><PlayRateBadge value={row.playRate} /></div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full h-9 border-[#E5EAF3]"
                      onClick={() => setDetailRow(row)}
                    >
                      {translateInterfaceText("Ver detalhes")}</Button>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 pt-4 mt-2 border-t border-[#EEF2F7]">
                  <p className="text-[12px] text-[#64748B]">
                    {translateInterfaceText("Mostrando")}{" "}<span className="font-medium text-[#0F172A]">{(safePage - 1) * PAGE_SIZE + 1}</span>
                    {'–'}
                    <span className="font-medium text-[#0F172A]">{Math.min(safePage * PAGE_SIZE, filteredRows.length)}</span>
                    {translateInterfaceText(" de ")}
                    <span className="font-medium text-[#0F172A]">{filteredRows.length}</span> {" "}{translateInterfaceText("URLs")}</p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 border-[#E5EAF3] text-[#334155] disabled:opacity-40"
                      disabled={safePage === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      {translateInterfaceText("Anterior")}</Button>
                    <div className="flex items-center gap-1 px-2">
                      {Array.from({ length: totalPages }).map((_, i) => {
                        const n = i + 1;
                        const isEdge = n === 1 || n === totalPages;
                        const isNear = Math.abs(n - safePage) <= 1;
                        if (!isEdge && !isNear) {
                          if (n === 2 || n === totalPages - 1) {
                            return <span key={n} className="text-[#94A3B8] text-[12px] px-1">…</span>;
                          }
                          return null;
                        }
                        return (
                          <button
                            key={n}
                            onClick={() => setPage(n)}
                            className={`min-w-[32px] h-8 px-2 rounded-lg text-[13px] font-medium transition-colors ${
                              n === safePage
                                ? 'bg-[#2563FF] text-white'
                                : 'text-[#334155] hover:bg-[#F1F5F9]'
                            }`}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 border-[#E5EAF3] text-[#334155] disabled:opacity-40"
                      disabled={safePage === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      {translateInterfaceText("Próxima")}</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail modal */}
        <Dialog open={!!detailRow} onOpenChange={(open) => !open && setDetailRow(null)}>
          <DialogContent className="sm:max-w-[520px] rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-[18px] font-semibold text-[#0F172A]">
                {translateInterfaceText("Detalhes da referência")}</DialogTitle>
              <DialogDescription className="text-[13px] text-[#64748B]">
                {translateInterfaceText("Resumo de desempenho desta URL no período selecionado.")}</DialogDescription>
            </DialogHeader>
            {detailRow && (
              <div className="space-y-4 py-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-[#64748B] mb-1">{translateInterfaceText("URL")}</div>
                  <div className="text-[14px] text-[#0F172A] break-all">{detailRow.url}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Metric label={translateInterfaceText("Plays")} value={formatNumber(detailRow.plays)} />
                  <Metric label={translateInterfaceText("Plays únicos")} value={formatNumber(detailRow.uniquePlays)} />
                  <Metric label={translateInterfaceText("Visualizações")} value={formatNumber(detailRow.impressions)} />
                  <Metric label={translateInterfaceText("Visualizações únicas")} value={formatNumber(detailRow.uniqueImpressions)} />
                  <Metric label={translateInterfaceText("Play Rate")} value={`${detailRow.playRate.toFixed(1).replace('.', ',')}%`} />
                </div>
                <div className="rounded-xl bg-[#EEF4FF] border border-[#DBE6FF] p-3 text-[13px] text-[#1E40AF]">
                  {translateInterfaceText("Essa referência representa")}{" "}{topShare.toFixed(1).replace('.', ',')}{translateInterfaceText("% dos plays do período.")}</div>
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => detailRow && handleCopy(detailRow.url)}>
                <Copy className="w-4 h-4 mr-1.5" /> {" "}{translateInterfaceText("Copiar URL")}</Button>
              <Button
                className="bg-[#2563FF] hover:bg-[#1E50D6] text-white"
                onClick={() => detailRow && handleOpen(detailRow.url)}
              >
                <ExternalLink className="w-4 h-4 mr-1.5" /> {" "}{translateInterfaceText("Abrir página")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => { useLanguage(); return ((
  <div className="rounded-xl border border-[#E5EAF3] p-3">
    <div className="text-[11px] text-[#64748B] uppercase tracking-wide">{label}</div>
    <div className="text-[16px] font-semibold text-[#0F172A] mt-1">{value}</div>
  </div>
)); };

const RowActions: React.FC<{
  row: ReferenceRow;
  onCopy: (url: string) => void;
  onOpen: (url: string) => void;
  onDetails: (row: ReferenceRow) => void;
}> = ({ row, onCopy, onOpen, onDetails }) => { useLanguage(); return ((
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-[#64748B] hover:text-[#0F172A]">
        <MoreHorizontal className="w-4 h-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-44">
      <DropdownMenuItem onClick={() => onOpen(row.url)}>
        <ExternalLink className="w-4 h-4 mr-2" /> {translateInterfaceText("Abrir URL")}</DropdownMenuItem>
      <DropdownMenuItem onClick={() => onCopy(row.url)}>
        <Copy className="w-4 h-4 mr-2" /> {" "}{translateInterfaceText("Copiar URL")}</DropdownMenuItem>
      <DropdownMenuItem onClick={() => onDetails(row)}>
        <Eye className="w-4 h-4 mr-2" /> {" "}{translateInterfaceText("Ver detalhes")}</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)); };

const EmptyState: React.FC = () => { useLanguage(); return ((
  <div className="flex flex-col items-center justify-center text-center min-h-[360px] py-10">
    <div className="w-16 h-16 rounded-full bg-[#EEF4FF] flex items-center justify-center mb-4">
      <ExternalLink className="w-7 h-7 text-[#2563FF]" />
    </div>
    <h4 className="text-[16px] font-semibold text-[#0F172A]">{translateInterfaceText("Sem dados de referência no período")}</h4>
    <p className="text-[13px] text-[#64748B] mt-2 max-w-md">
      {translateInterfaceText("Ainda não identificamos sites ou páginas enviando tráfego para este vídeo. Quando houver acessos externos, as referências aparecerão aqui.")}</p>
    <Button variant="outline" size="sm" className="mt-4 border-[#E5EAF3]">
      <RefreshCw className="w-4 h-4 mr-1.5" />
      {translateInterfaceText("Atualizar dados")}</Button>
  </div>
)); };
